/**
 * Alerts and notifications (Functional Specifications 13.1 and 13.2; FR-TRV-01 to FR-TRV-03,
 * FR-TRV-06, FR-PAR-32, FR-PAR-34, FR-PAR-40, FR-COL-04). Pure rules shared by the API, which raises and
 * e-mails the alerts, and the web application, which lists them: families and kinds, the site
 * settings, the expiry stages, who receives an alert, and its wording in French, Arabic and
 * English.
 */
import { z } from 'zod';
import { OTHER_DOCUMENT_KIND_LABELS } from './other-documents.js';
import { daysUntil } from './validity.js';
export const ALERT_FAMILIES = [
    'DOCUMENT_EXPIRY',
    'TRACEABILITY',
    'STOCK',
    'EVENT',
    'PICKUP_REQUEST',
];
/** A site may switch these off; document expiry and traceability deadlines stay on (FR-TRV-02). */
export const OPTIONAL_ALERT_FAMILIES = ['STOCK', 'EVENT', 'PICKUP_REQUEST'];
export const ALERT_KINDS = [
    'CAP_EXPIRY',
    'AUTHORIZATION_EXPIRY',
    'OTHER_DOCUMENT_EXPIRY',
    'ADR_EVIDENCE_EXPIRY',
    'CONTRACT_EXPIRY',
    'RETURN_OVERDUE',
    'TREATMENT_OVERDUE',
    'CERTIFICATE_MISSING',
    'STORAGE_OVERDUE',
    'STOCK_THRESHOLD',
    'REFUSAL',
    'PARTIAL_REFUSAL',
    'REFUSAL_FOLLOW_UP',
    'REVISION',
    'EXEMPTION',
    'IMPORT_REJECTED',
    'PICKUP_REQUEST',
    'PICKUP_REQUEST_DECISION',
];
export const ALERT_FAMILY_OF = {
    CAP_EXPIRY: 'DOCUMENT_EXPIRY',
    AUTHORIZATION_EXPIRY: 'DOCUMENT_EXPIRY',
    OTHER_DOCUMENT_EXPIRY: 'DOCUMENT_EXPIRY',
    ADR_EVIDENCE_EXPIRY: 'DOCUMENT_EXPIRY',
    CONTRACT_EXPIRY: 'DOCUMENT_EXPIRY',
    RETURN_OVERDUE: 'TRACEABILITY',
    TREATMENT_OVERDUE: 'TRACEABILITY',
    CERTIFICATE_MISSING: 'TRACEABILITY',
    STORAGE_OVERDUE: 'STOCK',
    STOCK_THRESHOLD: 'STOCK',
    REFUSAL: 'EVENT',
    PARTIAL_REFUSAL: 'EVENT',
    REFUSAL_FOLLOW_UP: 'TRACEABILITY',
    REVISION: 'EVENT',
    EXEMPTION: 'EVENT',
    IMPORT_REJECTED: 'EVENT',
    PICKUP_REQUEST: 'PICKUP_REQUEST',
    PICKUP_REQUEST_DECISION: 'PICKUP_REQUEST',
};
export const ALERT_SEVERITIES = ['INFO', 'WARNING', 'CRITICAL'];
/** Roles a site may name as recipients of a family. */
export const ALERT_RECIPIENT_ROLES = [
    'SITE_ADMIN',
    'SITE_MANAGER',
    'SITE_OPERATOR',
    'SITE_READER',
    'TENANT_ADMIN',
    'GROUP_ANALYST',
];
/** CAP alerts go to the responsable déchets and the site managers (FR-PAR-40). */
export const DEFAULT_ALERT_RECIPIENTS = {
    DOCUMENT_EXPIRY: ['SITE_MANAGER', 'SITE_ADMIN'],
    TRACEABILITY: ['SITE_MANAGER', 'SITE_ADMIN'],
    STOCK: ['SITE_MANAGER', 'SITE_OPERATOR'],
    EVENT: ['SITE_MANAGER', 'SITE_ADMIN'],
    PICKUP_REQUEST: ['SITE_MANAGER'],
};
const recipientRoles = z.array(z.enum(ALERT_RECIPIENT_ROLES)).min(1).max(6);
/** The alert settings of a site, set by its administrator (FR-TRV-02, FR-TRV-03). */
export const alertSettingsSchema = z.object({
    /** Days before an expiry when the alert starts, then escalates (60 and 30 by default). */
    expiryWarningDays: z.array(z.number().int().min(1).max(365)).min(1).max(4),
    /** Hour of the daily digest, in the site's time zone. */
    digestHour: z.number().int().min(0).max(23),
    /**
     * FR-TRV-05: days after which an alert nobody acknowledged goes to the company coordinators
     * (site administrators of the company, else the tenant administrators); 0 switches it off.
     */
    escalationDays: z.number().int().min(0).max(60),
    disabledFamilies: z.array(z.enum(OPTIONAL_ALERT_FAMILIES)).max(3),
    recipients: z.object({
        DOCUMENT_EXPIRY: recipientRoles,
        TRACEABILITY: recipientRoles,
        STOCK: recipientRoles,
        EVENT: recipientRoles,
        PICKUP_REQUEST: recipientRoles,
    }),
});
export const DEFAULT_ALERT_SETTINGS = {
    expiryWarningDays: [60, 30],
    digestHour: 7,
    escalationDays: 7,
    disabledFamilies: [],
    recipients: DEFAULT_ALERT_RECIPIENTS,
};
/** The stored settings of a site completed with the defaults; thresholds sorted, widest first. */
export function resolveAlertSettings(stored) {
    const s = stored ?? {};
    const days = [...new Set(s.expiryWarningDays?.length ? s.expiryWarningDays : [60, 30])].sort((a, b) => b - a);
    return {
        expiryWarningDays: days,
        digestHour: s.digestHour ?? DEFAULT_ALERT_SETTINGS.digestHour,
        escalationDays: s.escalationDays ?? DEFAULT_ALERT_SETTINGS.escalationDays,
        disabledFamilies: s.disabledFamilies ?? [],
        recipients: { ...DEFAULT_ALERT_RECIPIENTS, ...s.recipients },
    };
}
/**
 * Where a dated document stands against the warning thresholds: nothing before the widest one,
 * then one stage per threshold (the last one a warning, the others information), critical on the
 * day it expires and after (FR-PAR-32: 60 and 30 days before, and on the day).
 */
export function expiryStage(validTo, today, thresholds = DEFAULT_ALERT_SETTINGS.expiryWarningDays) {
    const daysLeft = daysUntil(validTo, today);
    if (daysLeft < 0)
        return { stage: 'EXPIRED', severity: 'CRITICAL', daysLeft };
    if (daysLeft === 0)
        return { stage: 'TODAY', severity: 'CRITICAL', daysLeft };
    const ascending = [...new Set(thresholds)].sort((a, b) => a - b);
    const threshold = ascending.find((d) => daysLeft <= d);
    if (threshold === undefined)
        return null;
    return {
        stage: `D${threshold}`,
        severity: threshold === ascending[0] ? 'WARNING' : 'INFO',
        daysLeft,
    };
}
/**
 * Alerts that reach a user through a role rather than a site's recipients: the tenant
 * administrators manage the prestataires, so they receive the expiry of their authorizations and
 * documents (FR-PAR-32, FR-PAR-34) and the imports of the organisation; finance, who negotiates
 * the contracts, receives their expiry (FR-SUI-10).
 */
export function receivesByRole(user, alert) {
    const admin = user.tenantRoles.includes('TENANT_ADMIN');
    switch (alert.kind) {
        case 'AUTHORIZATION_EXPIRY':
            return admin;
        case 'OTHER_DOCUMENT_EXPIRY':
        case 'ADR_EVIDENCE_EXPIRY':
            return admin && alert.entityType === 'prestataire';
        case 'IMPORT_REJECTED':
            return admin && !alert.siteIds.length;
        case 'CONTRACT_EXPIRY':
            return (user.tenantRoles.includes('FINANCE') ||
                alert.siteIds.some((id) => user.siteRoles[id]?.includes('FINANCE')));
        default:
            return false;
    }
}
/**
 * Whether a user receives an alert: the named users when there are some (a requester is always
 * told of the decision, FR-COL-04), otherwise the users holding a recipient role of the alert's
 * family on one of its sites, where the family is on, and those who receive it by role. A site
 * digest leaves the latter out (`byRole: false`): they would get it once per site.
 */
export function isAlertRecipient(user, alert, settingsOf, { byRole = true } = {}) {
    if (user.userId && alert.raisedBy === user.userId)
        return false;
    if (alert.userIds.length)
        return Boolean(user.userId && alert.userIds.includes(user.userId));
    if (byRole && receivesByRole(user, alert))
        return true;
    const family = ALERT_FAMILY_OF[alert.kind];
    return alert.siteIds.some((siteId) => {
        const settings = settingsOf(siteId);
        if (settings.disabledFamilies.includes(family))
            return false;
        const roles = [...user.tenantRoles, ...(user.siteRoles[siteId] ?? [])];
        return roles.some((r) => settings.recipients[family].includes(r));
    });
}
export const alertSnoozeSchema = z.object({ days: z.number().int().min(1).max(30) });
export const alertListQuerySchema = z.object({
    /** open: to handle; done: acknowledged, snoozed or resolved in the last 30 days. */
    view: z.enum(['open', 'done']).default('open'),
    siteId: z.string().uuid().optional(),
});
const localized = (max) => z.object({
    fr: z.string().trim().min(3).max(max),
    ar: z.string().trim().max(max).optional(),
    en: z.string().trim().max(max).optional(),
});
/** A regulatory bulletin published by the vendor to every tenant (FR-TRV-06). */
export const bulletinCreateSchema = z.object({
    title: localized(200),
    summary: localized(2000),
    body: z.string().trim().max(20000).optional(),
    links: z
        .array(z.object({ label: z.string().trim().min(1).max(200), url: z.string().url() }))
        .max(20)
        .default([]),
    effectiveOn: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/)
        .optional(),
    /** Impacted features and datasets, e.g. "Catalogue CMD", "Filières". */
    impacts: z.array(z.string().trim().min(1).max(80)).max(20).default([]),
    /** FR-TRV-07: the reference-data version the bulletin announces, for the impacts report. */
    refVersionId: z.string().uuid().optional(),
});
const str = (v) => (v === null || v === undefined ? '' : String(v));
/** DD/MM/YYYY, the way dates are written on Moroccan forms. */
export function formatAlertDate(v) {
    const s = str(v);
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
    return m ? `${m[3]}/${m[2]}/${m[1]}` : s;
}
function tonnes(v, locale) {
    const n = Number(v);
    if (!Number.isFinite(n))
        return str(v);
    return n.toLocaleString(locale === 'en' ? 'en-GB' : 'fr-FR', { maximumFractionDigits: 3 });
}
/** The French agrees with the document: "le CAP" is masculine, "l'autorisation" feminine. */
function expiryPhrase(stage, p, feminine = false) {
    const date = formatAlertDate(p.validTo);
    const days = Math.abs(Number(p.daysLeft ?? 0));
    if (stage === 'EXPIRED') {
        return {
            fr: `${feminine ? 'expirée' : 'expiré'} depuis le ${date}`,
            ar: `منتهي الصلاحية منذ ${date}`,
            en: `expired since ${date}`,
        };
    }
    if (stage === 'TODAY') {
        return { fr: `expire aujourd'hui`, ar: `تنتهي صلاحيته اليوم`, en: `expires today` };
    }
    return {
        fr: `expire dans ${days} jour${days > 1 ? 's' : ''} (${date})`,
        ar: `تنتهي صلاحيته خلال ${days} يومًا (${date})`,
        en: `expires in ${days} day${days > 1 ? 's' : ''} (${date})`,
    };
}
export const ALERT_FAMILY_LABELS = {
    DOCUMENT_EXPIRY: { fr: 'Documents à renouveler', ar: 'وثائق للتجديد', en: 'Documents to renew' },
    TRACEABILITY: { fr: 'Délais de traçabilité', ar: 'آجال التتبع', en: 'Traceability deadlines' },
    STOCK: { fr: 'Stock sur site', ar: 'المخزون في الموقع', en: 'Stock on site' },
    EVENT: { fr: 'Événements', ar: 'أحداث', en: 'Events' },
    PICKUP_REQUEST: { fr: "Demandes d'enlèvement", ar: 'طلبات الرفع', en: 'Pickup requests' },
};
const AUTHORIZATION_KINDS = {
    COLLECTE_TRANSPORT: {
        fr: 'Collecte-transport',
        ar: 'الجمع والنقل',
        en: 'Collection and transport',
    },
    INSTALLATION_TRAITEMENT: {
        fr: 'Installation de traitement',
        ar: 'منشأة المعالجة',
        en: 'Treatment installation',
    },
    INSTALLATION_STOCKAGE: {
        fr: 'Installation de stockage',
        ar: 'منشأة التخزين',
        en: 'Storage installation',
    },
    DECHARGE_CLASSE_3: { fr: 'Décharge de classe 3', ar: 'مطرح من الصنف 3', en: 'Class 3 landfill' },
};
/** Items of the pre-departure checklist a site manager may exempt (FR-FIA-23). */
const CHECKLIST_ITEMS = {
    BSD_PRINTED: { fr: 'BSD imprimés', ar: 'السندات مطبوعة', en: 'BSD printed' },
    LABELS_PRINTED: { fr: 'étiquettes', ar: 'الملصقات', en: 'labels' },
    DECLARATION_PRINTED: { fr: 'déclaration ADR', ar: 'تصريح ADR', en: 'ADR declaration' },
    CONSIGNE_PRINTED: {
        fr: 'consignes écrites',
        ar: 'التعليمات الكتابية',
        en: 'written instructions',
    },
    TRANSPORTER_AUTHORIZED: {
        fr: 'transporteur autorisé',
        ar: 'الناقل المرخص',
        en: 'authorized carrier',
    },
};
/** The page of the web application that shows the object of an alert. */
export function alertPath(a) {
    const site = a.siteIds?.[0] ? `site=${a.siteIds[0]}` : '';
    const q = (extra) => {
        const parts = [extra, site].filter(Boolean).join('&');
        return parts ? `?${parts}` : '';
    };
    switch (a.entityType) {
        case 'cap':
            return `/caps${q('')}`;
        case 'prestataire':
            return `/prestataires/${a.entityId}${q('')}`;
        case 'expedition':
            return `/expeditions${q(`open=${a.entityId}`)}`;
        case 'chargement':
            return `/chargements/${a.entityId}${q('')}`;
        case 'pickup_request':
            return `/pickup-requests${q('')}`;
        case 'waste':
            return `/stock${q('')}`;
        case 'other_document':
            return `/other-documents${q('')}`;
        case 'contract':
            return `/contracts${q(`open=${a.entityId}`)}`;
        case 'import_batch':
            return `/imports/${a.entityId}${q('')}`;
        default:
            return `/${q('')}`;
    }
}
/** Documents whose French name is feminine, for the agreement of "expirée". */
const FEMININE_DOCUMENTS = new Set([
    'ASSURANCE',
    'ACCREDITATION_LABORATOIRE',
    'ADR_FICHE_SECURITE',
]);
const COST_KINDS = {
    TRANSPORT: { fr: 'transport', ar: 'النقل', en: 'transport' },
    TRAITEMENT: { fr: 'traitement', ar: 'المعالجة', en: 'treatment' },
    TAXE: { fr: 'taxe', ar: 'الرسوم', en: 'tax' },
    ACHAT_CONDITIONNEMENT: {
        fr: 'achat de conditionnement',
        ar: 'شراء التغليف',
        en: 'container purchase',
    },
    RECETTE: { fr: 'recette', ar: 'مداخيل', en: 'revenue' },
    AUTRE_OPERATION: { fr: 'autre opération', ar: 'عملية أخرى', en: 'other operation' },
};
const IMPORT_KIND_LABELS = {
    ORIGINS: { fr: 'des origines', ar: 'المصادر', en: 'origins' },
    WASTES: { fr: 'des déchets', ar: 'النفايات', en: 'wastes' },
    CONDITIONNEMENTS: { fr: 'des conditionnements', ar: 'التعبئة', en: 'containers' },
    PRESTATAIRES: { fr: 'des prestataires', ar: 'مقدمي الخدمات', en: 'service providers' },
    AUTHORIZATIONS: { fr: 'des autorisations', ar: 'التراخيص', en: 'authorizations' },
    CAPS: { fr: 'des CAP', ar: 'شهادات القبول المسبق', en: 'CAP' },
    FILIERES: { fr: 'des filières', ar: 'المسارات', en: 'routes' },
    USERS: { fr: 'des utilisateurs', ar: 'المستخدمين', en: 'users' },
    RETURNS: { fr: 'des retours de BSD', ar: 'عودة السندات', en: 'BSD returns' },
    INVOICES: {
        fr: 'des factures prestataires',
        ar: 'فواتير مقدمي الخدمات',
        en: 'provider invoices',
    },
    EXPEDITIONS: {
        fr: "de l'historique des expéditions",
        ar: 'السجل التاريخي للشحنات',
        en: 'expédition history',
    },
    STOCK_WEIGHINGS: {
        fr: 'des pesées de production',
        ar: 'أوزان الإنتاج',
        en: 'production weighings',
    },
    EXPEDITION_WEIGHINGS: {
        fr: 'des pesées des expéditions',
        ar: 'أوزان الشحنات',
        en: 'expédition weighings',
    },
};
/** "n rejected rows" in Arabic: the dual, the plural from 3 to 10, the singular from 11 on. */
function arabicRejectedRows(n) {
    const r = n % 100;
    if (n === 1)
        return 'سطر واحد مرفوض';
    if (n === 2)
        return 'سطران مرفوضان';
    if (r >= 3 && r <= 10)
        return `${n} أسطر مرفوضة`;
    if (r >= 11)
        return `${n} سطرًا مرفوضًا`;
    return `${n} سطر مرفوض`;
}
const REQUEST_STATUS = {
    ACCEPTED: { fr: 'acceptée', ar: 'مقبول', en: 'accepted' },
    REFUSED: { fr: 'refusée', ar: 'مرفوض', en: 'refused' },
    PLANNED: { fr: 'planifiée', ar: 'مبرمج', en: 'planned' },
    CANCELLED: { fr: 'annulée', ar: 'ملغى', en: 'cancelled' },
};
/**
 * The title and the detail line of an alert in the reader's language, from its kind, stage and
 * the values stored when it was raised. Used by the notification centre and the e-mails.
 */
export function describeAlert(alert, locale = 'fr') {
    const p = alert.params;
    const pick = (t) => t[locale] ?? t.fr;
    const by = str(p.byName);
    switch (alert.kind) {
        case 'CAP_EXPIRY': {
            const phrase = pick(expiryPhrase(alert.stage, p));
            // some sites number their CAP "CAP-2025-0142": no need to say it twice
            const cap = /^CAP\b/i.test(str(p.number)) ? str(p.number) : `CAP ${str(p.number)}`;
            return {
                title: pick({
                    fr: `${cap} ${phrase}`,
                    ar: `شهادة القبول ${str(p.number)} ${phrase}`,
                    en: `${cap} ${phrase}`,
                }),
                detail: [
                    [str(p.wasteName), str(p.installationName)].filter(Boolean).join(' → '),
                    p.renewalNumber
                        ? pick({
                            fr: `renouvellement ${str(p.renewalNumber)} en attente du volet B`,
                            ar: `التجديد ${str(p.renewalNumber)} في انتظار الجزء ب`,
                            en: `renewal ${str(p.renewalNumber)} awaiting part B`,
                        })
                        : '',
                ]
                    .filter(Boolean)
                    .join(' · '),
            };
        }
        case 'AUTHORIZATION_EXPIRY': {
            const phrase = pick(expiryPhrase(alert.stage, p, true));
            return {
                title: pick({
                    fr: `Autorisation ${str(p.number)} (${str(p.prestataireName)}) ${phrase}`,
                    ar: `ترخيص ${str(p.number)} (${str(p.prestataireName)}) ${phrase}`,
                    en: `Authorization ${str(p.number)} (${str(p.prestataireName)}) ${phrase}`,
                }),
                detail: str(p.installationName) ||
                    (AUTHORIZATION_KINDS[str(p.authorizationKind)]
                        ? pick(AUTHORIZATION_KINDS[str(p.authorizationKind)])
                        : str(p.authorizationKind)),
            };
        }
        case 'OTHER_DOCUMENT_EXPIRY':
        case 'ADR_EVIDENCE_EXPIRY': {
            const kind = str(p.documentKind);
            const phrase = pick(expiryPhrase(alert.stage, p, FEMININE_DOCUMENTS.has(kind)));
            const label = OTHER_DOCUMENT_KIND_LABELS[kind]?.[locale] ??
                OTHER_DOCUMENT_KIND_LABELS.AUTRE[locale];
            const holder = p.prestataireName ? ` (${str(p.prestataireName)})` : '';
            return {
                title: `${label} ${str(p.reference)}${holder} ${phrase}`,
                detail: alert.kind === 'ADR_EVIDENCE_EXPIRY'
                    ? pick(p.prestataireName
                        ? {
                            fr: 'Preuve ADR du transporteur',
                            ar: 'إثبات ADR الخاص بالناقل',
                            en: "The carrier's ADR evidence",
                        }
                        : {
                            fr: 'Preuve ADR du site',
                            ar: 'إثبات ADR الخاص بالموقع',
                            en: "The site's ADR evidence",
                        })
                    : str(p.notes),
            };
        }
        case 'CONTRACT_EXPIRY': {
            const phrase = pick(expiryPhrase(alert.stage, p));
            const reference = p.number ? ` ${str(p.number)}` : '';
            const kind = COST_KINDS[str(p.contractKind)];
            return {
                title: pick({
                    fr: `Contrat${reference} avec ${str(p.prestataireName)} ${phrase}`,
                    ar: `العقد${reference} مع ${str(p.prestataireName)} ${phrase}`,
                    en: `Contract${reference} with ${str(p.prestataireName)} ${phrase}`,
                }),
                detail: [kind ? pick(kind) : str(p.contractKind), str(p.label), str(p.companyName)]
                    .filter(Boolean)
                    .join(' · '),
            };
        }
        case 'RETURN_OVERDUE':
            return {
                title: pick({
                    fr: `BSD ${str(p.bsdNumber)} : retour attendu depuis le ${formatAlertDate(p.dueOn)}`,
                    ar: `السند ${str(p.bsdNumber)}: العودة منتظرة منذ ${formatAlertDate(p.dueOn)}`,
                    en: `BSD ${str(p.bsdNumber)}: return expected since ${formatAlertDate(p.dueOn)}`,
                }),
                detail: [str(p.wasteName), str(p.destinationName)].filter(Boolean).join(' · '),
            };
        case 'TREATMENT_OVERDUE':
            return {
                title: pick({
                    fr: `BSD ${str(p.bsdNumber)} : traitement attendu depuis le ${formatAlertDate(p.dueOn)}`,
                    ar: `السند ${str(p.bsdNumber)}: المعالجة منتظرة منذ ${formatAlertDate(p.dueOn)}`,
                    en: `BSD ${str(p.bsdNumber)}: treatment expected since ${formatAlertDate(p.dueOn)}`,
                }),
                detail: [str(p.wasteName), str(p.destinationName)].filter(Boolean).join(' · '),
            };
        case 'CERTIFICATE_MISSING':
            return {
                title: pick({
                    fr: `BSD ${str(p.bsdNumber)} : certificat d'élimination attendu depuis le ${formatAlertDate(p.dueOn)}`,
                    ar: `السند ${str(p.bsdNumber)}: شهادة الإزالة منتظرة منذ ${formatAlertDate(p.dueOn)}`,
                    en: `BSD ${str(p.bsdNumber)}: certificat d'élimination expected since ${formatAlertDate(p.dueOn)}`,
                }),
                detail: pick({
                    fr: [
                        str(p.wasteName),
                        str(p.destinationName),
                        `traité le ${formatAlertDate(p.treatedAt)}`,
                    ]
                        .filter(Boolean)
                        .join(' · '),
                    ar: [str(p.wasteName), str(p.destinationName), `عولج في ${formatAlertDate(p.treatedAt)}`]
                        .filter(Boolean)
                        .join(' · '),
                    en: [
                        str(p.wasteName),
                        str(p.destinationName),
                        `treated on ${formatAlertDate(p.treatedAt)}`,
                    ]
                        .filter(Boolean)
                        .join(' · '),
                }),
            };
        case 'STORAGE_OVERDUE':
            return {
                title: pick({
                    fr: `${str(p.wasteName)} : en stock depuis ${str(p.ageDays)} jours (maximum ${str(p.maxDays)})`,
                    ar: `${str(p.wasteName)}: في المخزون منذ ${str(p.ageDays)} يومًا (الحد ${str(p.maxDays)})`,
                    en: `${str(p.wasteName)}: in stock for ${str(p.ageDays)} days (maximum ${str(p.maxDays)})`,
                }),
                detail: pick({
                    fr: `${tonnes(p.balanceT, 'fr')} t, la plus ancienne entrée du ${formatAlertDate(p.oldestDate)}`,
                    ar: `${tonnes(p.balanceT, 'ar')} طن، أقدم إدخال بتاريخ ${formatAlertDate(p.oldestDate)}`,
                    en: `${tonnes(p.balanceT, 'en')} t, oldest entry on ${formatAlertDate(p.oldestDate)}`,
                }),
            };
        case 'STOCK_THRESHOLD':
            return {
                title: pick({
                    fr: `${str(p.wasteName)} : ${tonnes(p.balanceT, 'fr')} t en stock, au-delà du seuil de ${tonnes(p.maxT, 'fr')} t`,
                    ar: `${str(p.wasteName)}: ${tonnes(p.balanceT, 'ar')} طن في المخزون، فوق العتبة ${tonnes(p.maxT, 'ar')} طن`,
                    en: `${str(p.wasteName)}: ${tonnes(p.balanceT, 'en')} t in stock, above the ${tonnes(p.maxT, 'en')} t threshold`,
                }),
                detail: pick({
                    fr: 'Planifiez un enlèvement',
                    ar: 'برمج عملية رفع',
                    en: 'Plan a pickup',
                }),
            };
        case 'REFUSAL':
            return {
                title: pick({
                    fr: `BSD ${str(p.bsdNumber)} refusé par ${str(p.destinationName)}`,
                    ar: `السند ${str(p.bsdNumber)} مرفوض من ${str(p.destinationName)}`,
                    en: `BSD ${str(p.bsdNumber)} refused by ${str(p.destinationName)}`,
                }),
                detail: str(p.reason),
            };
        case 'PARTIAL_REFUSAL':
            return {
                title: pick({
                    fr: `BSD ${str(p.bsdNumber)} : ${tonnes(p.quantityRefusedT, 'fr')} t refusées par ${str(p.destinationName)}`,
                    ar: `السند ${str(p.bsdNumber)}: ${tonnes(p.quantityRefusedT, 'ar')} طن مرفوضة من ${str(p.destinationName)}`,
                    en: `BSD ${str(p.bsdNumber)}: ${tonnes(p.quantityRefusedT, 'en')} t refused by ${str(p.destinationName)}`,
                }),
                detail: str(p.reason),
            };
        case 'REFUSAL_FOLLOW_UP':
            return {
                title: pick({
                    fr: `BSD ${str(p.bsdNumber)} refusé : décidez du devenir du déchet`,
                    ar: `السند ${str(p.bsdNumber)} مرفوض: قرر مصير النفاية`,
                    en: `BSD ${str(p.bsdNumber)} refused: decide what becomes of the waste`,
                }),
                detail: [
                    p.quantityT !== undefined && p.quantityT !== null
                        ? `${tonnes(p.quantityT, locale)} ${locale === 'ar' ? 'طن' : 't'}`
                        : '',
                    str(p.destinationName),
                    str(p.reason),
                ]
                    .filter(Boolean)
                    .join(' · '),
            };
        case 'REVISION':
            return {
                title: pick({
                    fr: `BSD ${str(p.bsdNumber)} révisé`,
                    ar: `تمت مراجعة السند ${str(p.bsdNumber)}`,
                    en: `BSD ${str(p.bsdNumber)} revised`,
                }),
                detail: [str(p.reason), by].filter(Boolean).join(' — '),
            };
        case 'EXEMPTION': {
            const rules = Array.isArray(p.rules)
                ? p.rules
                    .map((r) => (CHECKLIST_ITEMS[str(r)] ? pick(CHECKLIST_ITEMS[str(r)]) : str(r)))
                    .join(', ')
                : '';
            const on = str(p.bsdNumber);
            return {
                title: pick({
                    fr: `Dérogation ${rules} ${on ? `sur le BSD ${on}` : 'sur un chargement'}`,
                    ar: `استثناء ${rules} ${on ? `على السند ${on}` : 'على شحنة'}`,
                    en: `Exemption ${rules} ${on ? `on BSD ${on}` : 'on a chargement'}`,
                }),
                detail: [str(p.reason), by].filter(Boolean).join(' — '),
            };
        }
        case 'IMPORT_REJECTED': {
            const n = Number(p.rejected ?? 0);
            const what = IMPORT_KIND_LABELS[str(p.importKind)];
            return {
                title: pick({
                    fr: `Import ${what ? what.fr : str(p.importKind)} : ${n} ligne${n > 1 ? 's' : ''} rejetée${n > 1 ? 's' : ''}`,
                    ar: `استيراد ${what ? what.ar : str(p.importKind)}: ${arabicRejectedRows(n)}`,
                    en: `Import of ${what ? what.en : str(p.importKind)}: ${n} row${n > 1 ? 's' : ''} rejected`,
                }),
                detail: [str(p.fileName), by].filter(Boolean).join(' — '),
            };
        }
        case 'PICKUP_REQUEST':
            return {
                title: pick({
                    fr: `Nouvelle demande d'enlèvement ${str(p.number)}`,
                    ar: `طلب رفع جديد ${str(p.number)}`,
                    en: `New pickup request ${str(p.number)}`,
                }),
                detail: [
                    str(p.originName),
                    p.wantedDate
                        ? pick({
                            fr: `souhaitée le ${formatAlertDate(p.wantedDate)}`,
                            ar: `مطلوب يوم ${formatAlertDate(p.wantedDate)}`,
                            en: `wanted on ${formatAlertDate(p.wantedDate)}`,
                        })
                        : '',
                    by,
                ]
                    .filter(Boolean)
                    .join(' · '),
            };
        case 'PICKUP_REQUEST_DECISION': {
            const status = pick(REQUEST_STATUS[str(p.status)] ?? {
                fr: str(p.status),
                ar: str(p.status),
                en: str(p.status),
            });
            const planned = p.plannedDate ? ` (${formatAlertDate(p.plannedDate)})` : '';
            return {
                title: pick({
                    fr: `Votre demande ${str(p.number)} est ${status}${planned}`,
                    ar: `طلبك ${str(p.number)} ${status}${planned}`,
                    en: `Your request ${str(p.number)} is ${status}${planned}`,
                }),
                detail: [str(p.reason), by].filter(Boolean).join(' — '),
            };
        }
    }
}
//# sourceMappingURL=alerts.js.map